

/**
 * PUserSettings component - Client component for user settings popup
 * Displays user information and provides options for accessing settings and logging out
 */
import { IconSettings, IconLogout, IconRefresh } from "@tabler/icons-react";
import { Card } from "../../Card/Card";
import { IconButton } from "../../Buttons/IconButton";
import { UserIcon } from "../../UserIcon/UserIcon";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { isSamePath } from "@/lib/routing/activePath";
import { useAuth } from "@/lib/auth/AuthContext";
import { User } from "../../../types/User";
import { useNavigate as useRouter, useLocation } from "react-router-dom";
import { PEditUserColor } from "../../PEditUserColor/PEditUserColor";
import { AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { setOnboardingState } from "@/lib/api/handlers/OnboardingHandling";
import { COACH_RESTART_FLAG_KEY } from "@/lib/onboarding/coach-store";
import { useOnboardingOptional } from "@/providers/OnboardingProvider";

/**
 * Props for the PUserSettings component
 */
type PUserSettingsProps = {
  ref?: React.RefObject<HTMLDivElement | null>; // Ref for detecting outside clicks
  user: User; // User data object
  onClose: () => void; // Optional callback for closing the popup
};

export const PUserSettings: React.FC<PUserSettingsProps> = ({
  ref,
  user,
  onClose,
}) => {
  const [userState, setUserState] = useState<User>(user);
  // State for tracking logout process
  const [loading, setLoading] = useState(false);
  const [pEditUserColorOpen, setPEditUserColorOpen] = useState<boolean>(false);
  // The onboarding coach is OWNER-only, so the "restart intro" entry is too —
  // hide it for everyone else rather than show a no-op control.
  //
  // The role is read from OnboardingProvider's already-loaded progress (same
  // source this used to fetch itself: getOnboarding().companyRole) and frozen
  // at OPEN time. The popup mounts fresh on every open, so a per-mount fetch
  // meant the entry was decided AFTER first paint and dropped into the middle
  // of the list a roundtrip later — shoving "Log Out" down under the cursor.
  // A snapshot can never insert a row into an open menu; the next open picks
  // up whatever the provider knows by then.
  const onboarding = useOnboardingOptional();
  const companyRoleAtOpen = onboarding?.progress?.companyRole;
  const [isOwner] = useState(
    () => companyRoleAtOpen?.toUpperCase() === "OWNER",
  );
  const router = useRouter();
  const pathname = useLocation().pathname;
  const { logout } = useAuth();
  // Get translations for the user settings popup
  const t = useTranslations("appbar");

  // Listen for navigation events
  useEffect(() => {
    const handleNavigationStart = () => {
      setLoading(true);
    };

    const handleNavigationEnd = () => {
      setLoading(false);
    };

    // Add event listeners
    window.addEventListener("navigationStart", handleNavigationStart);
    window.addEventListener("navigationEnd", handleNavigationEnd);

    // Clean up function
    return () => {
      window.removeEventListener("navigationStart", handleNavigationStart);
      window.removeEventListener("navigationEnd", handleNavigationEnd);
    };
  }, []);

  /**
   * Handles user logout process
   * Shows loading state during API call and redirects to login page on success
   */
  const handleLogout = async () => {
    setLoading(true);
    await logout();
    router("/auth/login");
  };

  /**
   * Re-open the onboarding coach the user previously dismissed. The coach has no
   * other reopen path: its X persists tourState=SKIPPED. We flip it back to
   * PENDING, set the restart flag (so a later reload's one-shot auto-graduate
   * doesn't immediately kill it again), then signal the app-level coach to
   * revive in-session.
   */
  const handleRestartIntro = async () => {
    // Persist PENDING first; only arm the flag + revive the coach if it stuck,
    // so a failed PUT (server stays SKIPPED) can't leave a flag that suppresses
    // the next session's auto-graduate with no coach to ever clear it.
    // welcomeShown=true rides along: older progress docs (pre-welcome-modal, or
    // seeded by completeStep/updateState upserts) carry welcomeShown=false and
    // nothing else ever flips it for a company with existing events — but the
    // coach's journeyActive gate requires it, so without this the restart is a
    // silent no-op. An explicit restart implies the user is past the welcome.
    const res = await setOnboardingState(undefined, undefined, true, "PENDING");
    if (res && typeof res === "object" && "isServerError" in res) {
      onClose();
      return;
    }
    try {
      localStorage.setItem(COACH_RESTART_FLAG_KEY, "1");
    } catch {
      // localStorage unavailable
    }
    window.dispatchEvent(new Event("onboarding-coach-restart"));
    window.dispatchEvent(new Event("onboarding-refetch"));
    // The coach only renders on routes with a matching journey stop; from the
    // settings/profile pages nothing would appear and the restart would feel
    // dead. Land on the dashboard root, where a stop always matches.
    if (pathname !== "/") router("/");
    onClose();
  };

  return createPortal(
    <Card
      ref={ref}
      width="w-60"
      rounded="rounded-[10px]"
      // Rail offset: track the avatar button, which the layout shell shifts
      // left while the AI drawer rail is open (see PBackgroundJobs).
      className="fixed top-12 right-2 sm:right-[calc(0.5rem+var(--ai-rail-push))] z-50"
      padding="p-2 space-y-6"
    >
      {/* User avatar with click handlers */}
      <UserIcon
        initialArgs={userState?.firstName?.at(0)}
        monogramColorArgs={userState?.monogramColor}
        size="w-16 h-16"
        fontSize="h1"
        onClickAction={() => {
          setPEditUserColorOpen(true);
        }}
        penAction={() => {
          setPEditUserColorOpen(true);
        }}
      />
      {/* User information section */}
      <div className="px-2">
        <p className="body-2-medium pb-1">
          {t("greeting")} {userState?.firstName}
        </p>
        <p className="body-3-regular font-secondary">{userState?.email}</p>
      </div>
      {/* Action buttons section */}
      <div className="space-y-2 w-full">
        <IconButton
          icon={IconSettings}
          text={t("settings")}
          href="/user/settings"
          onHoverAction={() => {
            
          }}
          onClickAction={() => {
            if (isSamePath(pathname, "/user/settings")) {
              onClose();
              return;
            }
            window.dispatchEvent(new Event("navigationStart"));
            router("/user/settings");
            onClose();
          }}
        />
        {isOwner && (
          <IconButton
            icon={IconRefresh}
            text={t("restartIntro")}
            onClickAction={handleRestartIntro}
          />
        )}
        <IconButton
          loading={loading}
          color="font-error"
          icon={IconLogout}
          text={t("logout")}
          onClickAction={handleLogout}
        />
      </div>
      <AnimatePresence>
        {pEditUserColorOpen && (
          <PEditUserColor
            setUserState={setUserState}
            onClose={() => setPEditUserColorOpen(false)}
            selectedColor={userState.monogramColor!}
            initial={user.firstName + (user.lastName ?? "")}
          />
        )}
      </AnimatePresence>
    </Card>,
    document.body,
  );
};
